import { SortField } from '../constants/SortFields';
import { BERTH_PREFERENCE_DEFAULT, GUEST_TRAVELLERS, LOGGED_IN_TRAVELLERS, STORAGE_CONSTANTS } from '../constants/TrainConstants';
import _get from 'lodash/get';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import _intersection from 'lodash/intersection';
import { monthNames } from '../constants/CalendarNames';
import fecha from 'fecha';
import { v4 as uuidv4 } from 'uuid';

export function generateId() {
  return uuidv4().split('-').pop();
}

export const stringCompare = (str1, str2) => (str1 && str2
  && typeof str1 === 'string' && typeof str2 === 'string'
  && str1.toLowerCase() === str2.toLowerCase());

const TATKAL_START_TIME = 10;
const TATKAL_END_TIME = 12;

const getTime = (time) => {
  const units = time.split(':');
  return (units[0] * 60) + units[1];
};

export const sortComparators = (sortField, isAscOrder) => {
  const order = isAscOrder ? 1 : -1;
  switch (sortField) {
  case SortField.name: {
    return (rail1, rail2) => order * (rail1.trainName > rail2.trainName ? 1 : -1);
  }
  case SortField.departure: {
    return (rail1, rail2) => order * (getTime(rail1.departureTime)
        - getTime(rail2.departureTime));
  }
  case SortField.duration: {
    return (rail1, rail2) => order * (rail1.duration - rail2.duration);
  }
  case SortField.arrival: {
    return (rail1, rail2) => order * (getTime(rail1.arrivalTime)
        - getTime(rail2.arrivalTime));
  }
  default:
    return () => 0;
  }
};

export const getShortName = (name, maxNameLength) => {
  let [ firstName, middleName, lastName ] = [ null, null, null ];
  const strs = name.split(' ', 3);
  switch (strs.length) {
  case 3:
    [ firstName, middleName, lastName ] = strs;
    break;
  case 2:
    [ firstName, lastName ] = strs;
    break;
  case 1:
    [ firstName ] = strs;
    break;
  default:
    break;
  }
  const fn = firstName ? firstName.trim()+' ' : '';
  const mn = middleName ? middleName.trim()+' ' : '';
  const ln = lastName ? lastName.trim() : '';
  if ((fn + mn + ln).length > maxNameLength) {
    if ((fn + ln).length > maxNameLength) {
      if (fn.length > maxNameLength) {
        return fn.substr(0, maxNameLength);
      } else {
        return (fn + ln).substr(0, maxNameLength);
      }
    } else {
      return (fn + mn + ln).substr(0, maxNameLength);
    }
  } else {
    return fn + mn + ln;
  }
};

export const getBerthPref = (berthPref, berthTypes) => {
  return berthTypes.includes(berthPref.id) ? berthPref : BERTH_PREFERENCE_DEFAULT;
};

export const isIRCTCTatkalTime = () => {
  const start = new Date();
  start.setHours(TATKAL_START_TIME, 0, 0, 0);

  const end = new Date();
  end.setHours(TATKAL_END_TIME, 0, 0, 0);

  const now = new Date();

  return now >= start && now <= end;
};

export const setCookie = (name, value, days) => {
  let expires = '';
  if (days) {
    let date = new Date();
    date.setTime(date.getTime() + (days*24*60*60*1000));
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + ';domain=.makemytrip.com;path=/';
};

export const getCookie = (name) => {
  let nameEQ = name + '=';
  let ca = document.cookie.split(';');
  for (let i=0;i < ca.length;i++) {
    let c = ca[i];
    while (c.charAt(0)===' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};
export const eraseCookie = (name) => {
  document.cookie = name+'=; Max-Age=-99999999;';
};

export function filterUnavailableClasses(trainsList = [], filterObject) {
  const isFilterApplied = _get(filterObject, 'quickFilter.filterTypes.available.selected', false);
  if (!isFilterApplied) { // if available filter is not selected then don't perform the filtering logic
    return trainsList;
  }
  const trainsListWithAvlabilityFilter = trainsList.map((train = {}) => {
    const { tbsAvailability = [] } = train;
    const filteredTbsAvailability = tbsAvailability.filter((tA = {}) => {
      const { availablityType } = tA;
      return availablityType === '1' || availablityType === '2' || !availablityType; // if we are not aware of the availability data then we should not hide the card
    });
    if (filteredTbsAvailability.length === 0) {
      return false; // if everything is filtered then return false so that _compact removes that index
    }
    return {
      ...train,
      tbsAvailability: filteredTbsAvailability,
    };
  });
  return _compact(trainsListWithAvlabilityFilter); // hide the train if all the classes are filtered
}

export const removeUnSelectedClasses = (train, selectedClassTypeArray) => {
  if (_isEmpty(selectedClassTypeArray)) {
    return train;
  }
  const selectedClassTypeList = _intersection(train.avlClasses, selectedClassTypeArray);
  return {
    ...train,
    avlClasses: selectedClassTypeList,
  };
};

export const getDateObjectFromDateAndTime = (date, datePattern, time, timePattern) => {
  const timeObject = fecha.parse(time, timePattern);
  const dateObject = fecha.parse(date, datePattern);
  dateObject.setHours(timeObject.getHours());
  dateObject.setMinutes(timeObject.getMinutes());
  return dateObject;
};

export const diffMinutes = (date1, date2) => (date1.getTime() - date2.getTime()) / (1000 * 60);

export const get12HourTime =(time) => `${time.substr(0, 2) % 12 === 0 ? 12 : time.substr(0, 2) % 12}:${time.substr(3)} ${time.substr(0, 2) > 11 ? 'PM' : 'AM'}`;

export const getBoardingStationString = (boardingStation, date, days) => {
  const boardingStationDate = new Date(date);
  boardingStationDate.setDate(boardingStationDate.getDate() + days);
  return `${boardingStation.stationName} (${boardingStation.stationCode}) - ${get12HourTime(boardingStation.departureTime)} (${boardingStationDate.getDate()} ${monthNames[boardingStationDate.getMonth()]})`;
};

export function capitaliseString(str) {
  try {
    return str
      .toLowerCase()
      .split(" ")
      .map((s) => (s[0].toUpperCase() + s.slice(1)))
      .join(" ");
  } catch (e) {
    return str;
  }
}

export const removePIIDataFromLS = () => {
  const piiDataKeys = [
    STORAGE_CONSTANTS.VERIFIED_USER_ID,
    STORAGE_CONSTANTS.EMAIL,
    STORAGE_CONSTANTS.MOBILE_NUMBER,
    STORAGE_CONSTANTS.NAME,
    LOGGED_IN_TRAVELLERS,
    GUEST_TRAVELLERS,
  ];
  piiDataKeys.map((key) => localStorage.removeItem(key));
  eraseCookie(STORAGE_CONSTANTS.VERIFIED_USER_ID);
}